import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { resetBoards } from "store/boardsSlice";
import {
  selectCurrentBoardData,
  updateCurrentBoard,
} from "store/currentBoardSlice";
import { addToast } from "store/toastsSlice";

import Api from "api/Api";

import Button from "layout/atoms/Button";
import ContentBox from "layout/atoms/ContentBox";
import Form from "layout/atoms/Form";
import Header from "layout/atoms/Header";
import Icon from "layout/atoms/Icon";
import InputRadio from "layout/atoms/InputRadio";
import InputText from "layout/atoms/InputText";
import MainContainer from "layout/atoms/MainContainer";
import Select from "layout/atoms/Select";
import Separator from "layout/atoms/Separator";

import { supportedLangsLong } from "i18n";
import CodeBlock from "layout/atoms/CodeBlock";
import ColorPicker from "layout/atoms/ColorPicker";

import { ReactComponent as PublicSvg } from "assets/icons/globe2.svg";
import { ReactComponent as PrivateSvg } from "assets/icons/lock.svg";

function BoardSettings() {
  const dispatch = useDispatch();
  const currentBoardData = useSelector(selectCurrentBoardData);
  const api = new Api();
  const { t } = useTranslation();

  // Separate state for each section
  const [nameLanguageData, setNameLanguageData] = useState({
    name: currentBoardData.name,
    shortDescription: currentBoardData.shortDescription || "",
    language: currentBoardData.language,
  });
  const [visibilityData, setVisibilityData] = useState({
    isPublic: currentBoardData.isPublic,
  });
  const [themeData, setThemeData] = useState({
    color: currentBoardData.color,
  });

  // Individual form for name and language
  const {
    register: registerNameLang,
    handleSubmit: handleSubmitNameLang,
    formState: formStateNameLang,
    formState: { errors: errorsNameLang, isDirty: isDirtyNameLang },
    reset: resetNameLang,
    setValue: setValueNameLang,
  } = useForm({
    defaultValues: {
      name: currentBoardData.name,
      shortDescription: currentBoardData.shortDescription || "",
      language: currentBoardData.language,
    },
  });

  useEffect(() => {
    setNameLanguageData({
      name: currentBoardData.name,
      shortDescription: currentBoardData.shortDescription || "",
      language: currentBoardData.language,
    });
    setVisibilityData({
      isPublic: currentBoardData.isPublic,
    });
    setThemeData({
      color: currentBoardData.color,
    });
    setValueNameLang("name", currentBoardData.name);
    setValueNameLang(
      "shortDescription",
      currentBoardData.shortDescription || "",
    );
    setValueNameLang("language", currentBoardData.language);
  }, [dispatch, currentBoardData, setValueNameLang]);

  // Name and Language section handlers
  const handleSaveNameLanguage = (data, e) => {
    const promises = [];
    let hasAnyChange = false;

    if (data.name !== currentBoardData.name) {
      hasAnyChange = true;
      promises.push(
        api.updateBoardName({ name: data.name }, currentBoardData.id),
      );
    }

    if (
      (data.shortDescription || "") !==
      (currentBoardData.shortDescription || "")
    ) {
      hasAnyChange = true;
      promises.push(
        api.updateBoardShortDescription(
          { shortDescription: data.shortDescription },
          currentBoardData.id,
        ),
      );
    }

    if (data.language !== currentBoardData.language) {
      hasAnyChange = true;
      promises.push(
        api.updateBoardLanguage(
          { language: data.language },
          currentBoardData.id,
        ),
      );
    }

    if (hasAnyChange) {
      Promise.all(promises)
        .then((responses) => {
          // Get the last response which contains the updated board data
          const lastResponse = responses[responses.length - 1];
          dispatch(resetBoards());
          dispatch(
            addToast({
              title: "toasts.dataSaved.title",
              description: "toasts.dataSaved.description",
              type: "success",
            }),
          );
          dispatch(
            updateCurrentBoard({
              ...data,
              publicUrl: lastResponse.publicUrl,
              theme: {
                primary: currentBoardData.color,
                primaryText: "ffffff",
                text: "000000",
                background: "ffffff",
              },
            }),
          );
          setNameLanguageData({
            name: data.name,
            shortDescription: data.shortDescription,
            language: data.language,
          });
          resetNameLang(data);
        })
        .catch((error) => {
          dispatch(
            addToast({
              title: "toasts.error.title",
              description: "toasts.error.description",
              type: "error",
            }),
          );
        });
    }
  };

  const handleSaveNameLanguageError = (errors) => {
    dispatch(
      addToast({
        title: "toasts.error.title",
        description: "toasts.error.description",
        type: "error",
      }),
    );
  };

  const handleCancelNameLanguage = () => {
    resetNameLang({
      name: currentBoardData.name,
      shortDescription: currentBoardData.shortDescription || "",
      language: currentBoardData.language,
    });
    setNameLanguageData({
      name: currentBoardData.name,
      shortDescription: currentBoardData.shortDescription || "",
      language: currentBoardData.language,
    });
  };

  // Visibility section handlers
  const handleSaveVisibility = () => {
    if (visibilityData.isPublic !== currentBoardData.isPublic) {
      api
        .updateBoardPrivacy(
          { isPublic: visibilityData.isPublic },
          currentBoardData.id,
        )
        .then((response) => {
          dispatch(resetBoards());
          dispatch(
            addToast({
              title: "toasts.dataSaved.title",
              description: "toasts.dataSaved.description",
              type: "success",
            }),
          );
          dispatch(
            updateCurrentBoard({
              name: currentBoardData.name,
              language: currentBoardData.language,
              isPublic: visibilityData.isPublic,
              publicUrl: response.publicUrl,
              theme: {
                primary: currentBoardData.color,
                primaryText: "ffffff",
                text: "000000",
                background: "ffffff",
              },
            }),
          );
        })
        .catch((error) => {
          dispatch(
            addToast({
              title: "toasts.error.title",
              description: "toasts.error.description",
              type: "error",
            }),
          );
        });
    }
  };

  const handleCancelVisibility = () => {
    setVisibilityData({ isPublic: currentBoardData.isPublic });
  };

  // Theme section handlers
  const handleSaveTheme = () => {
    if (themeData.color !== currentBoardData.color) {
      api
        .updateBoardTheme(
          {
            primary: themeData.color,
            primaryText: "ffffff",
            text: "000000",
            background: "ffffff",
          },
          currentBoardData.id,
        )
        .then((response) => {
          dispatch(resetBoards());
          dispatch(
            addToast({
              title: "toasts.dataSaved.title",
              description: "toasts.dataSaved.description",
              type: "success",
            }),
          );
          dispatch(
            updateCurrentBoard({
              name: currentBoardData.name,
              language: currentBoardData.language,
              isPublic: currentBoardData.isPublic,
              publicUrl: response.publicUrl,
              theme: {
                primary: themeData.color,
                primaryText: "ffffff",
                text: "000000",
                background: "ffffff",
              },
            }),
          );
        })
        .catch(() => {
          dispatch(
            addToast({
              title: "toasts.error.title",
              description: "toasts.error.description",
              type: "error",
            }),
          );
        });
    }
  };

  const handleCancelTheme = () => {
    setThemeData({ color: currentBoardData.color });
  };

  // Change detection for each section
  const hasNameLanguageChanges = isDirtyNameLang;
  const hasVisibilityChanges =
    visibilityData.isPublic !== currentBoardData.isPublic;
  const hasThemeChanges = themeData.color !== currentBoardData.color;

  return (
    <MainContainer
      topbar={
        <div>
          <h2>{t("boardsDetails.settings.header")}</h2>
        </div>
      }
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          maxWidth: "800px",
          marginBottom: "50px",
        }}
      >
        {/* Name and Language Section */}
        <ContentBox>
          <Header size="h3" margin="m">
            {t("boardsDetails.forms.settings.sections.general")}
          </Header>
          <Form
            onSubmit={handleSubmitNameLang(
              handleSaveNameLanguage,
              handleSaveNameLanguageError,
            )}
          >
            <InputText
              type="text"
              name="name"
              label={t("boardsDetails.forms.settings.fields.name.label")}
              register={registerNameLang}
              errors={errorsNameLang.name}
              validation={{
                required: true,
                minLength: 6,
                maxLength: 100,
              }}
            />
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "600",
                  marginBottom: "8px",
                  color: "var(--black)",
                }}
              >
                {t(
                  "boardsDetails.forms.settings.fields.shortDescription.label",
                )}
              </label>
              <textarea
                name="shortDescription"
                {...registerNameLang("shortDescription", { maxLength: 128 })}
                placeholder={t(
                  "boardsDetails.forms.settings.fields.shortDescription.placeholder",
                )}
                style={{
                  width: "100%",
                  minHeight: "80px",
                  padding: "10px 12px",
                  fontSize: "14px",
                  border: "1px solid #d0d7de",
                  borderRadius: "6px",
                  fontFamily: "inherit",
                  resize: "vertical",
                }}
              />
              {errorsNameLang.shortDescription && (
                <span
                  style={{
                    color: "#dd2b0e",
                    fontSize: "12px",
                    marginTop: "4px",
                  }}
                >
                  {t(
                    "boardsDetails.forms.settings.fields.shortDescription.maxLengthError",
                  )}
                </span>
              )}
            </div>
            <Select
              name="language"
              label={t("boardsDetails.forms.settings.fields.language.label")}
              register={registerNameLang}
              errors={errorsNameLang.language}
              options={supportedLangsLong}
            />
            {hasNameLanguageChanges && (
              <div style={{ justifyContent: "flex-start" }}>
                <Separator />
                <Button
                  type="button"
                  size="lg"
                  color="white"
                  onClick={handleCancelNameLanguage}
                >
                  {t("buttons.cancel")}
                </Button>
                <Button type="submit" size="lg" color="primary">
                  {t("buttons.save")}
                </Button>
              </div>
            )}
          </Form>
        </ContentBox>

        {/* Visibility Section */}
        <ContentBox>
          <Header size="h3" margin="m">
            {t("boardsDetails.forms.settings.sections.visibility")}
          </Header>
          <InputRadio
            name="isPublic"
            checked={!visibilityData.isPublic}
            onChange={() => setVisibilityData({ isPublic: false })}
          >
            <Icon src={PrivateSvg} />
            <div>
              <h5>
                {t("boardsDetails.forms.settings.fields.isPublic.private")}
              </h5>
              <p>
                {t("boardsDetails.forms.settings.fields.isPublic.privateDesc")}
              </p>
            </div>
          </InputRadio>
          <InputRadio
            name="isPublic"
            checked={visibilityData.isPublic}
            onChange={() => setVisibilityData({ isPublic: true })}
          >
            <Icon src={PublicSvg} />
            <div>
              <h5>
                {t("boardsDetails.forms.settings.fields.isPublic.public")}
              </h5>
              <p>
                {t("boardsDetails.forms.settings.fields.isPublic.publicDesc")}
              </p>
            </div>
          </InputRadio>
          {currentBoardData.publicUrl && (
            <div>
              <CodeBlock
                code={currentBoardData.publicUrl}
                title={t("boardsDetails.forms.settings.fields.publicUrl.title")}
                url={currentBoardData.publicUrl}
              />
            </div>
          )}
          {hasVisibilityChanges && (
            <div>
              <Separator />
              <Button
                type="button"
                size="lg"
                color="white"
                onClick={handleCancelVisibility}
              >
                {t("buttons.cancel")}
              </Button>
              <Button
                type="button"
                size="lg"
                color="primary"
                onClick={handleSaveVisibility}
              >
                {t("buttons.save")}
              </Button>
            </div>
          )}
        </ContentBox>

        {/* Theme Section */}
        <ContentBox>
          <Header size="h3" margin="m">
            {t("boardsDetails.forms.settings.sections.theme")}
          </Header>
          <div style={{ display: "block", width: "100%" }}>
            <ColorPicker
              label={t("boardsDetails.forms.settings.fields.color.label")}
              value={themeData.color}
              onChange={(data, color) =>
                setThemeData({ color: color.substring(1) })
              }
            />
          </div>
          {hasThemeChanges && (
            <div>
              <Separator />
              <Button
                type="button"
                size="lg"
                color="white"
                onClick={handleCancelTheme}
              >
                {t("buttons.cancel")}
              </Button>
              <Button
                type="button"
                size="lg"
                color="primary"
                onClick={handleSaveTheme}
              >
                {t("buttons.save")}
              </Button>
            </div>
          )}
        </ContentBox>
      </div>
    </MainContainer>
  );
}

export default BoardSettings;
